import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { bookingService } from '../../../_services/booking.service';
import { marketplaceService } from '../../../_services/marketplace.service';
import { hapticSuccess } from '../../../_utils/haptics';
import { addBookingToCalendar } from '../../../_utils/calendarEvent';
import { clientStartOf, clientMinutesOf, fmtTime12, headlineOf, isImg, initialsOf } from '../../clientDashboard/bookingHelpers';
import { WhenBlock, ServicesBlock, WhoRow, WhereBlock, ChangesBlock } from '../../clientDashboard/bookingSections';
import './BookingConfirmationPage.css';

/* The receipt: the page after a booking is made (signed in or guest) or after
   a client moves one. The tick and the headline are the moment; below them
   the same sections as the booking details sheet, in the same order: the
   salon photo, when, what you booked, who, where, the booking ID. */

const AnimatedCheckmark = () => (
  <div className="bc-check" aria-hidden="true">
    <svg viewBox="0 0 52 52" className="bc-check-svg">
      <circle className="bc-check-circle" cx="26" cy="26" r="25" fill="none" />
      <path className="bc-check-path" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
    </svg>
  </div>
);
const TickSmall = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 13l4 4L19 7" /></svg>
);

const firstNameOf = (name) => (name ? String(name).trim().split(/\s+/)[0] : '');
// loc arrives as [lng, lat] or GeoJSON { coordinates: [lng, lat] }
const coordsFromLoc = (loc) => {
  const c = Array.isArray(loc) ? loc : (loc && Array.isArray(loc.coordinates) ? loc.coordinates : null);
  if (!c || c.length < 2) return null;
  const lng = Number(c[0]); const lat = Number(c[1]);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};
const staffAvatarOf = (m) => {
  if (!m) return '';
  const ui = m.userImage || {};
  return ui.circleImage || ui.rectangleImage || ui.squareImage || m.socialAvatar || m.avatar || '';
};
const longDate = (d) => d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

const BookingConfirmationPage = () => {
  const { salonId, salonSlug, hairdresserId } = useParams();
  const location = useLocation();
  const history = useHistory();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Place + person from the public summaries (no auth). Never blocks the page.
  const [place, setPlace] = useState(null);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [claimSent, setClaimSent] = useState(false);
  const [claimSending, setClaimSending] = useState(false);

  // URL params
  const searchParams = new URLSearchParams(location.search);
  const bookingId = searchParams.get('bid');
  const paymentStatus = searchParams.get('payment'); // 'success' | 'captured' | null
  const fromMobile = searchParams.get('fromMobile') === '1';
  // Reschedule success (client dashboard): the same receipt, different words.
  // ?moved=1 flips the headline and the calendar pill; ?was= carries the old start.
  const moved = searchParams.get('moved') === '1';
  const wasIso = searchParams.get('was');

  // Guest booking (GUEST-BOOKING-PLAN.md): no session. The booking-scoped
  // manage token came back from the guest create and was stashed in
  // sessionStorage; the emailed manage link may also carry it as ?token=.
  // localStorage holds a copy too: in-app browsers (Instagram, 2026-10-19)
  // drop sessionStorage on the way back from Stripe.
  const isGuest = searchParams.get('guest') === '1' || !localStorage.getItem('accessToken');
  const manageToken = (() => {
    try {
      return searchParams.get('token')
        || sessionStorage.getItem(`guestManageToken:${bookingId}`)
        || localStorage.getItem(`guestManageToken:${bookingId}`)
        || null;
    } catch (e) { return null; }
  })();
  // Back from a completed Stripe checkout (the BE success_url adds these).
  // Stripe only sends people here once the card step succeeded, and the
  // booking was created before it, so it exists even if we can't read it.
  const backFromCheckout = searchParams.get('cd') === 'success' || !!searchParams.get('session_id');

  // Confirmation landed: success haptic in the native shell (silent no-op on
  // web). Also pin the page to the top: arriving by in-app push from the
  // dashboard, the picker's scroll lock hands back the old offset after the
  // route has changed, so the tick opened part way down the page.
  useEffect(() => {
    if (booking && !error) {
      hapticSuccess();
      try { window.scrollTo(0, 0); document.documentElement.scrollTop = 0; document.body.scrollTop = 0; } catch (e) { /* ignore */ }
    }
  }, [booking, error]);

  /* ----- Booking ----- */
  useEffect(() => {
    const fetchBookingDetails = async () => {
      if (!bookingId) { setError('No booking ID provided'); setLoading(false); return; }
      try {
        setLoading(true);
        // Client-facing API; guests authenticate with the booking-scoped token,
        // which the service prefers over any session in localStorage.
        const response = await bookingService.getClientBookingById(bookingId, manageToken);
        // v2 error envelope: { statusCode: 0, error: { errorCode, responseMessage } }
        const envErr = response.data?.error;
        if (response.data?.errorCode || envErr?.errorCode || response.data?.statusCode === 0) {
          setError(envErr?.responseMessage || response.data?.messages || 'Failed to load booking details');
        } else {
          let bookingData = response.data?.responseData || response.data?.data || response.data;
          // The booking record is nested under bookings[0] on this endpoint.
          if (bookingData && Array.isArray(bookingData.bookings) && bookingData.bookings.length) {
            bookingData = { ...bookingData, ...bookingData.bookings[0] };
          }
          setBooking(bookingData);
        }
      } catch (err) {
        console.error('Error fetching booking:', err);
        setError('Failed to load booking details');
      } finally {
        setLoading(false);
      }
    };
    fetchBookingDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, isGuest, manageToken]);

  /* ----- Place + person -----
     On a salon route the salon summary has everything in one call. On
     /book/pro/:id (no salon in the URL) the staff summary gives the person
     and the salon's id, then the salon summary adds the headline photo, the
     slug and the contact routes. */
  const salonRef = salonSlug || salonId || null;
  const stylistIdForLookup = hairdresserId || booking?.hairdresserId || null;
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        let res;
        if (salonRef) res = await marketplaceService.getSalonSummary({ salonId: salonRef });
        else if (stylistIdForLookup) res = await marketplaceService.getStaffSummary({ hairdresserId: stylistIdForLookup });
        if (cancelled || !res) return;
        let salon = res.salon || null;
        if (!salonRef && salon && (salon._id || salon.id)) {
          try {
            const more = await marketplaceService.getSalonSummary({ salonId: salon._id || salon.id });
            if (cancelled) return;
            if (more && more.salon) salon = { ...more.salon, ...salon, galleryImages: more.salon.galleryImages, image: more.salon.image || salon.image, slug: more.salon.slug || salon.slug, onlineBookingContactPhone: more.salon.onlineBookingContactPhone, onlineBookingContactEmail: more.salon.onlineBookingContactEmail, phoneNumber: more.salon.phoneNumber || salon.phoneNumber };
          } catch (e) { /* contact routes simply stay hidden */ }
        }
        const staff = res.staff || null;
        const team = res.team || [];
        const member = staff || team.find(m => m._id === stylistIdForLookup) || null;
        if (cancelled) return;
        setPlace({
          hero: headlineOf(salon),
          salonId: (salon && (salon._id || salon.id)) || '',
          slug: (salon && salon.slug) || '',
          salonName: salon?.name || salon?.salonName || '',
          address: salon?.formattedAddress || '',
          coords: coordsFromLoc(salon?.loc),
          salonImage: salon?.image || '',
          phone: salon?.onlineBookingContactPhone || salon?.phoneNumber || salon?.phone || '',
          email: salon?.onlineBookingContactEmail || '',
          stylist: member ? { name: member.name || '', avatar: staffAvatarOf(member), role: member.jobTitle || (member.isSalonOwner ? 'Owner' : 'Stylist') } : null,
        });
      } catch (e) {
        // Non-fatal: the page renders fully from the booking alone.
      }
    };
    if (salonRef || stylistIdForLookup) load();
    return () => { cancelled = true; };
  }, [salonRef, stylistIdForLookup]);

  /* ----- Guest to account (claim link). Framing is "keep your history",
     never "create an account to continue" (GUEST-BOOKING-PLAN.md §5). ----- */
  const handleClaimAccount = async () => {
    if (claimSending || claimSent) return;
    setClaimSending(true);
    try {
      let stashedEmail = null;
      try { stashedEmail = sessionStorage.getItem(`guestEmail:${bookingId}`); } catch (e) { /* ignore */ }
      const email = stashedEmail || booking?.clientEmail || booking?.email || booking?.userInfo?.email;
      if (!email) throw new Error('No email on booking');
      await bookingService.sendGuestClaimLink({ bookingId, email, manageToken });
      setClaimSent(true);
    } catch (e) {
      console.error('[guest] Could not send claim link:', e);
      alert('We could not send the link just now. Please try again in a moment.');
    } finally {
      setClaimSending(false);
    }
  };

  /* ----- Derived ----- */
  const services = booking?.services || [];
  // Whole visit (incl. processing + finishing), so the end time and the
  // calendar event are right for colour bookings too.
  const totalMinutes = booking ? clientMinutesOf(booking) : 0;
  const total = Number(booking?.totalAmount) || services.reduce((s, x) => s + (parseFloat(x.servicePrice) || 0), 0);
  const symbol = booking?.currencySymbol || '£';
  const depositAmount = parseFloat(booking?.depositAmount) || 0;
  const deposit = (paymentStatus === 'success' || booking?.isDepositPay) ? depositAmount : 0;
  const cardSaved = paymentStatus === 'captured';

  const salonName = place?.salonName || booking?.salonName || '';
  const address = place?.address || booking?.salonAddress || '';
  const stylistName = place?.stylist?.name || booking?.hairdresserName || '';
  const bookingAvatar = booking?.hairdresserImage?.circleImage || booking?.hairdresserImage?.rectangleImage || '';
  const stylistAvatar = place?.stylist?.avatar || (isImg(bookingAvatar) ? bookingAvatar : '');
  const bookingSalonImage = typeof booking?.salonImage === 'string' ? booking.salonImage : (booking?.salonImage?.url || '');
  const hero = place?.hero || (isImg(bookingSalonImage) ? bookingSalonImage : '');
  const hid = hairdresserId || booking?.hairdresserId || '';
  const slug = place?.slug || salonSlug || '';
  const sid = place?.salonId || salonId || '';
  const stylistUrl = !hid ? '' : slug ? `/book/${slug}/professional/${hid}` : `/book/pro/${hid}`;
  const salonUrl = slug ? `/book/${slug}` : sid ? `/salon/${sid}` : '';

  const clientFirstName = firstNameOf(booking?.user?.name || booking?.userInfo?.name || booking?.clientName || booking?.userName || '');
  const clientEmail = (() => {
    let stashed = null;
    try { stashed = sessionStorage.getItem(`guestEmail:${bookingId}`); } catch (e) { /* ignore */ }
    return stashed || booking?.clientEmail || booking?.email || booking?.userInfo?.email || booking?.user?.email || '';
  })();

  const headline = useMemo(() => {
    const who = clientFirstName ? `, ${clientFirstName}` : '';
    if (moved) return { title: `Your booking has moved${who}.`, sub: '' };
    if (paymentStatus === 'success') return { title: `You're booked${who}.`, sub: 'Your deposit is paid and your appointment is confirmed.' };
    if (paymentStatus === 'captured') return { title: `You're booked${who}.`, sub: 'Your card is saved for no-show protection. You will only be charged if you miss your appointment.' };
    return { title: `You're booked${who}.`, sub: 'Your appointment is confirmed.' };
  }, [clientFirstName, paymentStatus, moved]);

  const start = booking ? clientStartOf(booking) : null;
  const wasStr = (() => {
    if (!moved || !wasIso) return '';
    const d = new Date(wasIso);
    return Number.isNaN(d.getTime()) ? '' : `${longDate(d)}, ${fmtTime12(d)}`;
  })();

  /* ----- Actions ----- */
  // A calendar file rather than a Google Calendar link: the link needs a
  // Google session and means nothing to Apple Calendar or Outlook. The .ics
  // opens in whichever calendar the person uses, and re-adding a moved
  // booking updates the event (same UID, higher SEQUENCE).
  const handleAddToCalendar = () => {
    const end = new Date(start.getTime() + (totalMinutes || 60) * 60000);
    const serviceList = services.map(s => s.serviceName).filter(Boolean).join(', ') || 'Appointment';
    const url = `${window.location.origin}/dashboard/bookings/${booking._id || bookingId}`;
    return addBookingToCalendar({
      uid: `booking-${booking._id || bookingId}@beu.app`,
      sequence: moved ? Math.floor(Date.now() / 1000) : 0,
      start, end,
      title: `${serviceList} at ${salonName || 'the salon'}`,
      description: [`With ${stylistName || 'your stylist'}`, `Booking ID ${booking._id || bookingId}`, `Manage it at ${url}`].join('\n'),
      location: [salonName, address].filter(Boolean).join(', '),
      url,
      filename: `beu-booking-${String(booking._id || bookingId).slice(-8)}`,
    });
  };
  const copyId = () => navigator.clipboard.writeText(String(booking._id || bookingId));

  // REPLACE, not push: this page is a receipt, not a place to return to. If
  // we pushed, the profile page would arrive as an in-app push and its own
  // back arrow (history.goBack) would pop straight back onto this page.
  const handleBackToProfile = () => {
    if (!salonSlug && !salonId) { history.replace(hairdresserId ? `/book/pro/${hairdresserId}` : '/'); return; }
    const base = salonSlug ? `/book/${salonSlug}` : `/salon/${salonId}`;
    history.replace(hairdresserId ? `${base}/professional/${hairdresserId}` : base);
  };
  const handleViewBookings = () => history.push('/dashboard/bookings');

  /* ---------- Loading ---------- */
  if (loading) {
    return (
      <div className="bc-page" aria-busy="true" aria-label="Loading your booking">
        <div className="bc-content">
          <div className="bc-moment">
            <div className="bs-skel bc-skel--check" />
            <div className="bs-skel" style={{ width: 240, height: 30, marginTop: 20 }} />
            <div className="bs-skel" style={{ width: 200, height: 16, marginTop: 10 }} />
          </div>
          <div className="bs-skel bc-skel--hero" />
          <div className="bs-when"><div className="bs-skel" style={{ width: '70%', height: 26 }} /><div className="bs-skel" style={{ width: '40%', height: 18, marginTop: 10 }} /></div>
          <div className="bs-section"><div className="bs-skel" style={{ width: 130, height: 18 }} />{[1, 2].map(i => <div key={i} className="bs-skel" style={{ height: 15, marginTop: 14 }} />)}</div>
        </div>
      </div>
    );
  }

  /* ---------- Booked, but no way to read it here ---------- */
  // In-app browsers can lose the guest's token across the Stripe redirect.
  // The booking is made; say so instead of "couldn't find that booking".
  if ((error || !booking) && backFromCheckout) {
    return (
      <div className="bc-page">
        <div className="bc-content bc-rise">
          <header className="bc-moment">
            <AnimatedCheckmark />
            <h1 className="bc-h1">You're booked in</h1>
            <p className="bc-sub">Your booking went through. You'll get the details by email or text, with a link to view or change it.</p>
          </header>
          <button type="button" className="bc-btn bc-btn--primary" onClick={handleBackToProfile}>Done</button>
        </div>
      </div>
    );
  }

  /* ---------- Error ---------- */
  if (error || !booking) {
    return (
      <div className="bc-page">
        <div className="bc-content bc-error">
          <div className="bc-error-icon" aria-hidden="true">!</div>
          <h1 className="bc-h1">We couldn't find that booking</h1>
          <p className="bc-sub">{error || 'It may have been removed, or the link is out of date.'}</p>
          <button type="button" className="bc-btn bc-btn--primary" onClick={handleBackToProfile}>Go back</button>
        </div>
      </div>
    );
  }

  /* ---------- Page ---------- */
  const sub = moved ? (
    <>Now <strong>{longDate(start)}</strong> at <strong>{fmtTime12(start)}</strong>{stylistName ? ` with ${stylistName}` : ''}.{wasStr ? <> Was {wasStr}.</> : null}</>
  ) : (
    <>
      {headline.sub}
      {clientEmail ? <> We've sent the details to <strong>{clientEmail}</strong>.</> : null}
    </>
  );

  return (
    <div className="bc-page">
      <div className="bc-content bc-rise">

        {/* A. The moment */}
        <header className="bc-moment">
          <AnimatedCheckmark />
          <h1 className="bc-h1">{headline.title}</h1>
          <p className="bc-sub">{sub}</p>
        </header>

        {/* B. The place: the salon's headline photo, the same as the list and the details sheet */}
        <div className={`bc-hero${hero ? '' : ' bc-hero--empty'}`} aria-hidden="true">
          {hero && <img className={`bc-hero-img${heroLoaded ? ' is-loaded' : ''}`} src={hero} alt="" onLoad={() => setHeroLoaded(true)} onError={() => setPlace(p => (p ? { ...p, hero: '' } : p))} />}
          {!hero && <span className="bc-hero-initials">{initialsOf(salonName || stylistName)}</span>}
        </div>

        {/* C. When, what, who, where: the shared sections */}
        <WhenBlock start={start} minutes={totalMinutes} onAddToCalendar={handleAddToCalendar} calendarLabel={moved ? 'Update my calendar' : 'Add to calendar'} />
        {services.length > 0 && <ServicesBlock services={services} symbol={symbol} total={total} deposit={deposit} cardSaved={cardSaved} tax={booking?.tax} />}
        <WhoRow name={stylistName} avatar={stylistAvatar} role={place?.stylist?.role} onGo={stylistUrl ? () => history.push(stylistUrl) : null} />
        <WhereBlock salonName={salonName} address={address} image={place?.salonImage || bookingSalonImage} coords={place?.coords} phone={place?.phone} email={place?.email} onGo={salonUrl ? () => history.push(salonUrl) : null} />
        <ChangesBlock lines={[]} bookingId={booking._id || bookingId} onCopy={copyId} />

        {/* Return-to-app (mobile browser hand-off) */}
        {fromMobile && (
          <section className="bc-card">
            <p className="bc-card-text">You're viewing this in your browser. Tap below to return to the app.</p>
            <button type="button" className="bc-btn bc-btn--primary" onClick={() => { window.location.href = `beuapp://booking-success?bid=${bookingId}`; }}>
              Return to Beu App
            </button>
          </section>
        )}

        {/* D. The way out. Signed-in clients go to their bookings; guests (no
            dashboard) go back to the place they booked. */}
        <div className="bc-cta">
          {isGuest ? (
            <button type="button" className="bc-btn bc-btn--primary" onClick={handleBackToProfile}>Back to {salonName || 'salon'}</button>
          ) : (
            <button type="button" className="bc-btn bc-btn--primary" onClick={handleViewBookings}>View my bookings</button>
          )}
        </div>

        {/* E. Account nudge (guests only), secondary to the way out above */}
        {isGuest && (
          <section className="bc-card" aria-labelledby="bc-claim">
            <h3 id="bc-claim" className="bc-card-title">Keep this booking in one place</h3>
            <p className="bc-card-text">Set a password and it's already in your history. No forms to fill in.</p>
            {claimSent ? (
              <p className="bc-card-sent"><span className="bc-tick"><TickSmall /></span>Check your inbox, we've sent you a link to set your password.</p>
            ) : (
              <button type="button" className="bc-btn bc-btn--secondary" onClick={handleClaimAccount} disabled={claimSending}>
                {claimSending ? 'Sending…' : 'Set a password'}
              </button>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default BookingConfirmationPage;
